import { NoddiSwitch } from '@ui-components/Inputs/NoddiSwitch';
import { NoddiTooltip } from '@ui-components/Overlay/NoddiTooltip';
import { cn } from 'noddi-ui-common';
import type { ComponentPropsWithoutRef } from 'react';
import { useId } from 'react';
import { useFieldContext } from '../formContext';
import { TanstackFieldError } from '../TanstackFieldError';
import { useTanstackFieldAria } from '../useTanstackFieldAria';

type NoddiSwitchProps = ComponentPropsWithoutRef<typeof NoddiSwitch>;

type Props = Omit<NoddiSwitchProps, 'checked' | 'onCheckedChange'> & {
  label?: React.ReactNode;
  tooltip?: string;
  description?: React.ReactNode;
  /** `end` puts the switch after the label and description (settings-row layout). */
  switchPosition?: 'start' | 'end';
};

export const TanstackFormSwitch = ({
  label,
  tooltip,
  description,
  switchPosition = 'start',
  id: providedId,
  ...switchProps
}: Props) => {
  const field = useFieldContext<boolean>();
  const fieldAria = useTanstackFieldAria(field, switchProps['aria-describedby']);
  const generatedId = useId();
  const id = providedId || generatedId;

  const switchNode = (
    <NoddiSwitch
      {...switchProps}
      id={id}
      checked={field.state.value ?? false}
      onCheckedChange={(checked) => field.handleChange(checked)}
      {...fieldAria}
    />
  );
  const labelNode = label && (
    <div className='flex items-center gap-2'>
      <label
        className={cn(
          'cursor-pointer text-sm text-black md:text-base',
          switchPosition === 'start' ? 'leading-none' : 'leading-snug'
        )}
        htmlFor={id}
      >
        {label}
      </label>
      {tooltip && <NoddiTooltip content={tooltip} />}
    </div>
  );

  if (switchPosition === 'end') {
    return (
      <div>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex min-w-0 flex-col gap-1'>
            {labelNode}
            {description && <p className='text-xs text-gray-600'>{description}</p>}
          </div>
          {switchNode}
        </div>
        <TanstackFieldError field={field} />
      </div>
    );
  }

  return (
    <div>
      <div className='flex items-center gap-2'>
        {switchNode}
        {labelNode}
      </div>
      {description && <p className='mt-2 text-xs text-gray-600'>{description}</p>}
      <TanstackFieldError field={field} />
    </div>
  );
};
